import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { BORDER_STRONG, RADIUS_PILL, TAP, TEAL, TEXT } from "./tokens";

export interface SegmentedOption<V extends string = string> {
  value: V;
  /** Already translated. */
  label: string;
}

export interface SegmentedControlProps<V extends string = string> {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  /** Sticks to the top of the nearest scroll container (white background). */
  sticky?: boolean;
  /**
   * Wrap onto a second line instead of scrolling sideways. For a row that is
   * only slightly too wide (a wide viewport, or a longer translation) every
   * option stays on screen and nothing hides behind the fade.
   */
  wrap?: boolean;
  /** Used by assistive tech. */
  ariaLabel?: string;
}

/** Width (px) of the fade that hints at pills hidden past the right edge. */
const FADE_WIDTH = 40;

/**
 * A 2-4 way choice as a row of 44px pills. With more than three options the
 * row scrolls sideways with a hidden scrollbar instead of wrapping (or wraps
 * onto a second line, with `wrap`); with three or fewer the pills share the
 * width. When the row scrolls, the active pill is scrolled into view on mount
 * and whenever the value changes, and a fade on the right edge shows while
 * more pills are hidden past it.
 */
function SegmentedControl<V extends string = string>({
  options,
  value,
  onChange,
  sticky,
  wrap,
  ariaLabel,
}: SegmentedControlProps<V>) {
  const scrolls = options.length > 3 && !wrap;
  const rowRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateFade = useCallback(() => {
    const row = rowRef.current;
    if (!row || !scrolls) {
      setCanScrollRight(false);
      return;
    }
    setCanScrollRight(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
  }, [scrolls]);

  // Keep the active pill visible: on mount and on every value change.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || !scrolls) return;
    const active = row.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
    if (active) {
      const rowRect = row.getBoundingClientRect();
      const pillRect = active.getBoundingClientRect();
      const left = pillRect.left - rowRect.left + row.scrollLeft;
      const right = left + pillRect.width;
      if (left < row.scrollLeft) row.scrollLeft = Math.max(0, left - 8);
      else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth + 8;
    }
    updateFade();
  }, [value, scrolls, options.length, updateFade]);

  useEffect(() => {
    if (!scrolls) return;
    updateFade();
    window.addEventListener("resize", updateFade);
    return () => window.removeEventListener("resize", updateFade);
  }, [scrolls, updateFade]);

  return (
    <div
      style={{
        position: sticky ? "sticky" : "relative",
        ...(sticky ? { top: 0, zIndex: 5, backgroundColor: "#FFFFFF" } : {}),
      }}
    >
      <div
        ref={rowRef}
        role="radiogroup"
        aria-label={ariaLabel}
        className="flex items-center"
        onScroll={scrolls ? updateFade : undefined}
        style={{
          gap: "8px",
          ...(wrap
            ? { flexWrap: "wrap" as const, rowGap: "8px", minHeight: `${TAP}px` }
            : { height: `${TAP}px` }),
          overflowX: scrolls ? "auto" : "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              className="font-inter"
              onClick={() => onChange(option.value)}
              style={{
                flex: scrolls || wrap ? "none" : "1 1 0",
                height: `${TAP}px`,
                padding: "0 16px",
                borderRadius: RADIUS_PILL,
                fontSize: "14px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
                backgroundColor: active ? TEAL : "#FFFFFF",
                color: active ? "#FFFFFF" : TEXT,
                cursor: "pointer",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {scrolls && canScrollRight && (
        <div
          aria-hidden="true"
          data-segmented-fade=""
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: `${FADE_WIDTH}px`,
            pointerEvents: "none",
            background: "linear-gradient(to right, rgba(255,255,255,0), #FFFFFF)",
          }}
        />
      )}
    </div>
  );
}

export default SegmentedControl;
